// Recently-selected map locations, persisted to localStorage.
// Most-recent-first, capped at MAX. recents[0] doubles as "last location".

import { useCallback, useState } from 'react'

export interface Location {
  lat: number
  lon: number
  name: string | null
}

const KEY = 'ecmwf.recentLocations'
const MAX = 5

// Dedup key: locations are stored to 4dp, so round to match.
function key(lat: number, lon: number): string {
  return `${lat.toFixed(4)},${lon.toFixed(4)}`
}

export function loadRecents(): Location[] {
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter(
      (l): l is Location => typeof l?.lat === 'number' && typeof l?.lon === 'number',
    )
  } catch {
    return []
  }
}

function save(recents: Location[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(recents))
  } catch {
    // ignore quota / disabled storage
  }
}

// Move the location to the front (keeping any existing name), cap at MAX.
export function pushRecent(lat: number, lon: number): Location[] {
  const k = key(lat, lon)
  const existing = loadRecents()
  const prior = existing.find(l => key(l.lat, l.lon) === k)
  const entry: Location = { lat, lon, name: prior?.name ?? null }
  const recents = [entry, ...existing.filter(l => key(l.lat, l.lon) !== k)].slice(0, MAX)
  save(recents)
  return recents
}

// Backfill a resolved place name onto the matching entry.
export function updateRecentName(lat: number, lon: number, name: string): Location[] {
  const k = key(lat, lon)
  const recents = loadRecents().map(l =>
    key(l.lat, l.lon) === k ? { ...l, name } : l,
  )
  save(recents)
  return recents
}

// Reactive view over the persisted list for the (single) location picker.
export function useRecentLocations() {
  const [recents, setRecents] = useState<Location[]>(loadRecents)
  const push = useCallback((lat: number, lon: number) => setRecents(pushRecent(lat, lon)), [])
  const updateName = useCallback(
    (lat: number, lon: number, name: string) => setRecents(updateRecentName(lat, lon, name)),
    [],
  )
  return { recents, push, updateName }
}
